/**
 * Feuilles de brouillon dans l'éditeur V2 : données partagées par le panneau et
 * les outils de chaque question (une seule requête, même clé), et actions.
 */

import { useMemo } from 'react';
import { useIsMutating, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useExerciseCardLinks } from '@/lib/cards/student-skillcards';
import type { ExerciseBlock } from '@/lib/exercise-v2/types';
import {
  checkChoices,
  fetchScratchRows,
  generateQuestionSheet,
  orderedQuestionBlocks,
  questionQcm,
  saveSheet,
  setSheetStatus,
  sheetBlocker,
  sheetState,
  type GenerateOutcome,
  type QuestionQcm,
  type ScratchRow,
  type SheetState,
} from './scratch-admin';

export const scratchAdminKey = (exerciseId: string) => ['scratch-admin', exerciseId] as const;

export function useScratchRows(exerciseId: string, enabled = true) {
  return useQuery({
    queryKey: scratchAdminKey(exerciseId),
    enabled: enabled && !!exerciseId,
    staleTime: 30_000,
    queryFn: () => fetchScratchRows(exerciseId),
  });
}

/** Une question de l'exercice, vue par les feuilles de brouillon. */
export interface SheetQuestion {
  id: string;
  index: number;
  block: ExerciseBlock;
  /** reliée à une SKILLCARD publiée */
  linked: boolean;
  qcm: QuestionQcm;
  /** pourquoi aucune feuille n'est possible (sans IA), ou null */
  blocker: string | null;
  /** feuille NOUVEAU MOTEUR (celle que l'éditeur génère et publie) */
  row: ScratchRow | null;
  /** feuille de l'ANCIEN moteur, gardée pour les apps pas encore à jour (migration 0532) */
  legacy: ScratchRow | null;
  legacyState: SheetState;
  state: SheetState;
  /** la feuille ne correspond plus au QCM (choix modifiés depuis la génération) : à régénérer */
  stale: boolean;
  /** l'état à afficher (une feuille périmée est « à revoir ») */
  shown: SheetState;
}

/** Questions + feuilles + liens SKILLCARD de l'exercice. */
export function useSheetQuestions(exerciseId: string, blocks: ExerciseBlock[], enabled = true) {
  const rows = useScratchRows(exerciseId, enabled);
  const links = useExerciseCardLinks(exerciseId, enabled);
  const questions = useMemo<SheetQuestion[]>(() => {
    const linked = new Set((links.data ?? []).map((l) => l.question_id));
    // une ligne sans version (base d'avant 0532) : déduite du modèle
    const trackOf = (r: ScratchRow) =>
      r.track ??
      (r.model && typeof r.model === 'object' && 'kernel' in r.model ? 'kernel' : 'legacy');
    const kernelRows = new Map<string, ScratchRow>();
    const legacyRows = new Map<string, ScratchRow>();
    (rows.data ?? []).forEach((r) => {
      // la version du moteur v2 (lot V3) est tirée de celle du noyau : l'éditeur ne la montre pas
      if (trackOf(r) === 'packs') return;
      (trackOf(r) === 'kernel' ? kernelRows : legacyRows).set(r.question_block_id, r);
    });
    return orderedQuestionBlocks(blocks).map((block, index) => {
      const qcm = questionQcm(blocks, block.id);
      const row = kernelRows.get(block.id) ?? null;
      const legacy = legacyRows.get(block.id) ?? null;
      const blocker = sheetBlocker(qcm);
      const state = sheetState(row);
      const stale =
        !!row &&
        row.status !== 'ineligible' &&
        (!!blocker || checkChoices(row.model, qcm).length > 0);
      return {
        id: block.id,
        index,
        block,
        linked: linked.has(block.id),
        qcm,
        blocker,
        row,
        legacy,
        legacyState: sheetState(legacy),
        state,
        stale,
        shown: stale ? 'review' : blocker && !row ? 'none' : state,
      };
    });
  }, [blocks, links.data, rows.data]);
  return {
    questions,
    isLoading: rows.isLoading || links.isLoading,
    error: (rows.error ?? links.error) as Error | null,
    /** l'exercice a au moins une question reliée */
    anyLinked: questions.some((q) => q.linked),
  };
}

function useInvalidate(exerciseId: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: scratchAdminKey(exerciseId) });
    // aperçu du player (feuilles brouillon comprises)
    void qc.invalidateQueries({ queryKey: ['scratch-models', exerciseId] });
  };
}

export const scratchGenerateKey = (exerciseId: string) => ['scratch-generate', exerciseId] as const;

/** Une génération est-elle en cours pour cet exercice (panneau ou question) ? */
export function useIsGenerating(exerciseId: string): boolean {
  return useIsMutating({ mutationKey: scratchGenerateKey(exerciseId) }) > 0;
}

export type GenerateResult = GenerateOutcome & {
  /** feuille publiée gardée : la nouvelle proposition n'était pas validée */
  keptPublished?: boolean;
};

/**
 * Génère, passe à la porte et enregistre la feuille d'UNE question. Une feuille
 * PUBLIÉE n'est remplacée que par une proposition validée : sinon elle reste en
 * ligne et la proposition est écartée.
 */
export function useGenerateSheet(exerciseId: string) {
  const invalidate = useInvalidate(exerciseId);
  return useMutation({
    mutationKey: scratchGenerateKey(exerciseId),
    mutationFn: async (vars: {
      question: Pick<SheetQuestion, 'id' | 'index' | 'qcm' | 'row'>;
      customInstructions?: string;
      onAttempt?: (attempt: number) => void;
      /** admin : tous les essais en Opus */
      forceOpus?: boolean;
    }): Promise<GenerateResult> => {
      const outcome = await generateQuestionSheet({
        exerciseId,
        questionIndex: vars.question.index,
        questionId: vars.question.id,
        qcm: vars.question.qcm,
        customInstructions: vars.customInstructions,
        onAttempt: vars.onAttempt,
        ...(vars.forceOpus ? { forceOpus: true } : {}),
      });
      if (outcome.kind === 'not_linked') return outcome;
      if (vars.question.row?.status === 'published' && outcome.kind !== 'ready')
        return { ...outcome, keptPublished: true };
      await saveSheet(exerciseId, vars.question.id, outcome.row);
      return outcome;
    },
    onSettled: invalidate,
  });
}

/** Publier / désactiver / remettre en brouillon une ou plusieurs feuilles. */
export function useSetSheetStatus(exerciseId: string) {
  const invalidate = useInvalidate(exerciseId);
  return useMutation({
    mutationFn: (vars: { ids: string[]; status: 'published' | 'disabled' | 'draft' }) =>
      setSheetStatus(vars.ids, vars.status),
    onSettled: invalidate,
  });
}
